import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useFlags } from "@/lib/flags";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { cn } from "@/lib/utils";
import { Check, X, MessageSquare } from "lucide-react";

// Investors (FEATURE_INVESTORS; backend/investor_routes.py; design_handoff_team_roles/ screens 1, 2, 4 and 9): what
// founders share with them, and removing one. Used by Settings -> Investors, the Team page and the Invite dialog.

// the founders' switches, one set for every investor; Hiring costs needs Money
export const SHARES = [
  ["money", "Money", "Costs per objective: task budget, AI spent and total cost, with project and company totals and the last 6 months."],
  ["hiring_costs", "Hiring costs", "The fixed fees you agreed with independent professionals, per objective. Needs Money."],
  ["plan", "Plan, credits and invoices", "Your plan and renewal date, the AI credits balance and its monthly ins and outs, and invoices. Never card details or billing addresses."],
  ["reliability", "Reliability", "Server outages and agent and service errors, as counts and a rate. About systems, never people."],
  ["delivery", "Delivery times", "Time to start, to first report and to done, per objective and as medians. Never per person."],
];

// GET /settings/investors: the switches, what investors see with them, and the company's investors
export function useInvestors() {
  const on = useFlags().investors;
  const { data } = useQuery({ queryKey: ["investor-settings"], enabled: !!on,
                              queryFn: async () => (await api.get("/settings/investors")).data });
  return on ? data : null;
}

// "1 investor sees 5 of 5 things you can share."
export function investorLine(inv) {
  if (!inv?.investors?.length) return "No investors right now.";
  const n = Object.values(inv.sharing).filter(Boolean).length;
  return `${inv.investors.length === 1 ? "1 investor sees" : `${inv.investors.length} investors see`} ${n} of ${SHARES.length} things you can share.`;
}

// The Invite dialog's "What they'll see": a tick for what's shared now, a cross for what isn't
const SEE = [
  [null, "Progress and team", "Always shared: objectives, change report counts, names and titles"],
  ["money", "Money per objective", "Task budgets, AI spent, totals per project"],
  ["hiring_costs", "Hiring costs", "Agreed fees per objective"],
  ["plan", "Plan, credits and invoices", "No card details or billing address"],
  ["reliability", "Reliability", "Server outages and errors, never people"],
  ["delivery", "Delivery times", "Per objective, never per person"],
];

export function WhatTheySee({ sharing }) {
  return (
    <div className="flex flex-col gap-2.5 pt-4 border-t" data-testid="invite-investor-sees">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="font-display text-[15px] leading-[1.45] tracking-[0.02em] font-semibold">What they'll see</span>
        <Link to="/founder/settings#investors" className="text-sm underline underline-offset-2 hover:opacity-50">Change what investors see</Link>
      </div>
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {SEE.map(([k, label, help]) => {
          const on = !k || !!sharing?.[k];
          const Icon = on ? Check : X;
          return (
            <li key={label} className="flex items-start gap-2">
              <Icon className={cn("w-3.5 h-3.5 mt-0.5 shrink-0", on ? "text-gr-success" : "text-muted-foreground")} aria-label={on ? "Shared" : "Not shared"} />
              <span className="flex flex-col"><span className={on ? "font-semibold" : "font-semibold text-muted-foreground"}>{label}</span>
                <span className="text-sm text-muted-foreground">{help}</span></span>
            </li>
          );
        })}
        <li className="flex items-start gap-2 text-muted-foreground"><X className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          Never: sessions, change report summaries, your messages with the team, settings</li>
      </ul>
      <p className="m-0 flex items-start gap-2 text-sm text-muted-foreground"><MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        They can write to you in Messages. Investors are free and don't use a seat.</p>
    </div>
  );
}

// Remove an investor: their password again; access ends at once (POST /settings/investors/{id}/remove)
export function RemoveInvestorDialog({ investor, company, onClose }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: async (password) => (await api.post(`/settings/investors/${investor.id}/remove`, { password })).data,
    onSuccess: () => {
      ["investor-settings", "mailbox"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${investor.name} was removed. Their access ended now.`); onClose();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <ConfirmDestructive open={!!investor} onOpenChange={(v) => !v && onClose()} action="Remove investor" title={`Remove ${investor?.name}?`}
      warning="To give them access again, you send a new invite." pending={remove.isPending} onConfirm={(password) => remove.mutate(password)}>
      <p className="m-0 [text-wrap:pretty]">Their access to {company || "your company"}'s investor view ends at once, and they stop getting the weekly summary. Your messages with them stay in your Messages.</p>
    </ConfirmDestructive>
  );
}
